import { useState, useEffect, useCallback, useRef } from 'react';
import { LayoutDashboard, Wheat, HandCoins, Tractor, Wrench, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { AgriDashboardTab } from './AgriDashboardTab';
import { CampaignsTab } from './CampaignsTab';
import { SubsidiesTab } from './SubsidiesTab';
import { LivestockTab } from './LivestockTab';
import { EquipmentTab } from './EquipmentTab';
import { useAuth } from '../../contexts/AuthContext';
import { syncAgriToFinancial } from '../../utils/moduleSync';
import { supabase } from '../../lib/supabase';

const CURRENT_YEAR = new Date().getFullYear();

type Tab = 'dashboard' | 'campaigns' | 'subsidies' | 'livestock' | 'equipment';

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { id: 'campaigns', label: 'Cycles culturaux', icon: Wheat },
  { id: 'subsidies', label: 'Aides & subventions', icon: HandCoins },
  { id: 'livestock', label: 'Élevage', icon: Tractor },
  { id: 'equipment', label: 'Matériel', icon: Wrench },
];

const AGRI_TABLES = [
  'agri_campaigns',
  'agri_campaign_charges',
  'agri_subsidies',
  'agri_livestock_herds',
  'agri_livestock_charges',
  'agri_livestock_sales',
  'agri_equipment',
];

export function AgricultureModule() {
  const { effectiveUserId } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [syncing, setSyncing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerSync = useCallback((syncYear: number) => {
    if (!effectiveUserId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSyncing(true);
      await syncAgriToFinancial(effectiveUserId, syncYear);
      setSyncing(false);
    }, 800);
  }, [effectiveUserId]);

  useEffect(() => {
    triggerSync(year);
  }, [year, triggerSync]);

  useEffect(() => {
    if (!effectiveUserId) return;

    const channels = AGRI_TABLES.map(table =>
      supabase
        .channel(`agri-sync-${table}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table, filter: `user_id=eq.${effectiveUserId}` },
          () => { triggerSync(year); }
        )
        .subscribe()
    );

    return () => {
      channels.forEach(ch => supabase.removeChannel(ch));
    };
  }, [effectiveUserId, year, triggerSync]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agriculture</h1>
          <p className="text-sm text-gray-500">Pilotez votre exploitation — cultures, élevage, aides et matériel</p>
        </div>
        <div className="flex items-center gap-3">
          {syncing && (
            <span className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
              <RefreshCw className="w-3 h-3 animate-spin" />
              Sync budget & trésorerie...
            </span>
          )}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
            <button
              onClick={() => setYear(y => y - 1)}
              className="p-1 rounded hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-gray-700 w-12 text-center">{year}</span>
            <button
              onClick={() => setYear(y => y + 1)}
              disabled={year >= CURRENT_YEAR + 1}
              className="p-1 rounded hover:bg-gray-100 text-gray-500 transition-colors disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                active
                  ? 'bg-white text-green-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div>
        {activeTab === 'dashboard' && <AgriDashboardTab year={year} />}
        {activeTab === 'campaigns' && <CampaignsTab year={year} />}
        {activeTab === 'subsidies' && <SubsidiesTab year={year} />}
        {activeTab === 'livestock' && <LivestockTab year={year} />}
        {activeTab === 'equipment' && <EquipmentTab />}
      </div>
    </div>
  );
}
